import { Formik } from "formik";
import React, { useMemo } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import * as yup from "yup";

import {
    AmountField,
    FileField,
    FormContainer,
    FormFooter,
    SelectField,
} from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { CurrencyOptions } from "../../../constants/currencies";
import {
    MaxTotalFileSize,
    MinAmountLargerThanZero,
} from "../../../misc/validators";
import {
    BankAccount,
    FormSubmissionHandler,
    ISODate,
    UploadedFileWithSize,
} from "../../../types/common";

export type InvoiceFormValues = {
    createdAt?: ISODate;
    bankAccount: string;
    currency: string;
    amount: string;
    files: UploadedFileWithSize[];
};

const schema = yup.object().shape({
    bankAccount: yup.string().required(),
    currency: yup.string().required(),
    amount: yup.string().required("This field is required").test(MinAmountLargerThanZero),
    files: yup.array().min(1, "Upload file is required").test(MaxTotalFileSize),
});

type InvoiceFormProps = {
    bankAccountList: BankAccount[];
    values?: InvoiceFormValues;
    onCancel: () => void;
    onSubmit: FormSubmissionHandler<InvoiceFormValues>;
};

const InvoiceForm = ({
    bankAccountList,
    values,
    onCancel,
    onSubmit,
}: InvoiceFormProps): JSX.Element => {
    const beneficiaryOptions = useMemo<SelectOption[]>(
        () =>
            bankAccountList.map((it) => ({
                value: `${it.bankAccountHolderName}-${it.bankAccountNumber}`,
                label: `${it.bankAccountHolderName}\n${it.bankAccountNumber}`,
            })),
        [bankAccountList],
    );
    return (
        <FormContainer>
            <Formik
                validationSchema={schema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={onSubmit}
                initialValues={
                    values || {
                        bankAccount: "",
                        currency: "SGD",
                        amount: "",
                        files: [],
                    }
                }
            >
                {({ handleSubmit, values }) => {
                    const selectedBankAccount = bankAccountList.find((it) =>
                        (it.bankAccountHolderName + "-" + it.bankAccountNumber) === values?.bankAccount
                    );
                    const defaultValue = beneficiaryOptions.find((it) =>
                        it.value === (selectedBankAccount?.bankAccountHolderName + "-" + selectedBankAccount?.bankAccountNumber)
                    );
                    return (
                        <Form
                            autoComplete="off"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <h2 className="mb-3">
                                Beneficiary Details
                            </h2>
                            <SelectField
                                name="bankAccount"
                                label="Beneficiary Bank Account"
                                options={beneficiaryOptions}
                                tall
                                required={true}
                                defaultValue={defaultValue}
                            />
                            {selectedBankAccount && (
                                <>
                                    <p className="mb-2">
                                        Beneficiary Bank
                                    </p>
                                    <p className="fw-bold mb-5">
                                        {selectedBankAccount.swiftAddress} -{" "}
                                        {selectedBankAccount.bankName}
                                    </p>

                                    <p className="mb-2">
                                        Beneficiary Address
                                    </p>
                                    <p className="fw-bold mb-5">
                                        {selectedBankAccount.registeredAddress}
                                    </p>
                                </>
                            )}
                            <hr className="bg-border-grey my-3" />
                            <h2 className="mb-3">Invoices</h2>
                            <InputGroup hasValidation>
                                <SelectField
                                    name="currency"
                                    label="Currency"
                                    options={CurrencyOptions}
                                    required={true}
                                />
                                <AmountField name="amount" label="Amount" required={true}/>
                            </InputGroup>
                            <FileField name="files" required={true}/>
                            <hr className="bg-border-grey my-3" />
                            <FormFooter  className="d-flex justify-content-between">
                                <Button
                                    variant="light.tertiary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <Button variant="light.primary" type="submit">
                                    Next
                                </Button>
                            </FormFooter>
                        </Form>
                    );
                }}
            </Formik>
        </FormContainer>
    );
};

export default InvoiceForm;
